import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiDossierUpdate } from "../common/api/Dossier/update";
import { RootInterface } from "../common/interfaces/RootInterface";
import { ApiGetRegistrationLink } from "../common/api/RegistrationLink/Get";
import moment from "moment";
import {
    CreateAndUpdateArgs,
    UpdateArgs,
} from "../common/interfaces/DossierInterface";
import { ApiDossierDelete } from "../common/api/Dossier/delete";
import { ApiDossierCreate } from "../common/api/Dossier/create";
import { ApiDossierCreateAndUpdate } from "../common/api/Dossier/createAndUpdate";
import { formatCreateAndUpdateData } from "../common/helpers/formatCreateAndUpdateData";
import { ApiCaravanSendExplanationEmail } from "../common/api/Dossier/caravanSendExplanationEmail";
import { AxiosResponse } from "axios";

export const caravanSendExplanationEmail = createAsyncThunk(
    "root/caravanSendExplanationEmail",
    async (
        args: {
            dossierUuid: string;
            dossierAccessToken: string;
            explanationCaravanDamage: string;
            establishmentId: number;
        },
        { getState, dispatch },
    ) => {
        const { root } = getState() as { root: RootInterface };
        const response: AxiosResponse = await ApiCaravanSendExplanationEmail(
            args.dossierUuid,
            args.dossierAccessToken,
            root.dossier?.dossier_id,
            args.explanationCaravanDamage,
            args.establishmentId,
            dispatch,
        );
        return response.data.status.message;
    },
);

export const getRegistrationLink = createAsyncThunk(
    "root/getRegistrationLink",
    async (slug: string, { dispatch }) => {
        const response = await ApiGetRegistrationLink(slug, dispatch);
        return {
            ...response.data.status.message,
            slug: slug,
        };
    },
);

export const deleteDossier = createAsyncThunk(
    "root/deleteDossier",
    async (
        args: { dossierUuid: string; fundingType: string },
        { dispatch },
    ) => {
        const response: AxiosResponse = await ApiDossierDelete(
            args.dossierUuid,
            args.fundingType,
            dispatch,
        );

        if (response.data?.status?.message) {
            return response.data.status.message;
        }

        return response;
    },
);

export const updateDossier = createAsyncThunk(
    "root/updateDossier",
    async (args: UpdateArgs, { getState, dispatch }) => {
        const { root } = getState() as { root: RootInterface };

        if (root.dossier?.dossier_id && root.dossier?.dossier_access_token) {
            const response: AxiosResponse = await ApiDossierUpdate(
                root.dossier.dossier_id,
                root.dossier.dossier_access_token,
                dispatch,
                args,
            );
            return response.data.status.message;
        }

        if (root.dossier?.car_license_plate || root.dossier?.person?.email) {
            const createAndUpdate: CreateAndUpdateArgs =
                formatCreateAndUpdateData(root.dossier);

            if (!createAndUpdate?.data) {
                return null;
            }

            const replaceIndex = createAndUpdate.data.findIndex(
                (obj: { method: string | undefined }) =>
                    obj.method === args.method,
            );

            if (replaceIndex === -1) {
                createAndUpdate.data.push(args);
            } else {
                createAndUpdate.data[replaceIndex] = args;
            }

            const response: AxiosResponse = await ApiDossierCreateAndUpdate(
                root.registrationLink.slug,
                dispatch,
                createAndUpdate,
            );
            return response.data.status.message;
        }

        const response: AxiosResponse = await ApiDossierCreate(
            root.registrationLink.slug,
            dispatch,
            args,
        );
        return response.data.status.message;
    },
);

const initialState: RootInterface = {
    finished: false,
    enableResume: false,
    modals: {
        help: false,
        greenCard: false,
        smartPhoto: false,
        mobileNumberCheck: false,
        bovagModal: false,
    },
    termsAccepted: 0,
    bovagAccepted: 0,
    privacyAccepted: 0,
    dossierLoading: false,
    dossierIsDeleted: false,
    dossierCreating: false,
    dossierError: {
        show: false,
        message: "",
    },
    registrationLink: {
        button_color_dark: "",
        button_color_light: "",
        primary_color: "",
        background_color: "",
        secondary_color: "",
        background_light: "",
        logo_url: "",
        notFound: false,
        loading: false,
        privacy_agreement_url: "",
        terms_and_condition_business_url: "",
        terms_and_condition_private_url: "",
        terms_and_condition_business_mobile_url: "",
        terms_and_condition_private_mobile_url: "",
        thank_you_page_url: "",
        slug: "",
        no_lease: false,
        private_lease: false,
        business_lease: false,
        private_insurance: false,
        hail_damage_enabled: false,
    },
    overview: {
        show: false,
    },
    getParams: {},
    expiresAt: moment().add(1, "day").toISOString(),
    dossierOptions: {
        allowsAppointments: false,
        allowsLoanVehicles: false,
        thankYouMessage: "",
        sendImageCaptureRequest: true,
    },
};

export const rootSlice = createSlice({
    name: "root",
    initialState,
    reducers: {
        setFinished: (state, action: PayloadAction<boolean>) => {
            state.finished = action.payload;
        },
        disableModals: (state) => {
            state.modals.help = false;
            state.modals.greenCard = false;
            state.modals.smartPhoto = false;
            state.modals.mobileNumberCheck = false;
            state.modals.bovagModal = false;
        },
        enableHelpModal: (state) => {
            state.modals.help = true;
            state.modals.greenCard = false;
            state.modals.smartPhoto = false;
            state.modals.mobileNumberCheck = false;
            state.modals.bovagModal = false;
        },
        enableGreenCardModal: (state) => {
            state.modals.greenCard = true;
            state.modals.help = false;
            state.modals.smartPhoto = false;
            state.modals.mobileNumberCheck = false;
            state.modals.bovagModal = false;
        },
        enableSmartPhotoModal: (state) => {
            state.modals.greenCard = false;
            state.modals.help = false;
            state.modals.smartPhoto = true;
            state.modals.mobileNumberCheck = false;
            state.modals.bovagModal = false;
        },
        enableBovagModal: (state) => {
            state.modals.bovagModal = true;
            state.modals.greenCard = false;
            state.modals.help = false;
            state.modals.smartPhoto = false;
            state.modals.mobileNumberCheck = false;
        },
        enableMobileNumberCheckModal: (state) => {
            state.modals.greenCard = false;
            state.modals.help = false;
            state.modals.smartPhoto = false;
            state.modals.mobileNumberCheck = true;
            state.modals.bovagModal = false;
        },
        toggleBovagTerms: (state) => {
            state.bovagAccepted = state.bovagAccepted === 1 ? 0 : 1;
        },
        setBovagToTrue: (state) => {
            state.bovagAccepted = 1;
        },
        togglePrivacy: (state) => {
            state.privacyAccepted = state.privacyAccepted === 1 ? 0 : 1;
        },
        toggleOverview: (state) => {
            state.overview.show = !state.overview.show;
        },
        setGetParams: (
            state,
            action: PayloadAction<RootInterface["getParams"]>,
        ) => {
            state.getParams = action.payload;
        },
        deleteDossierId: (state) => {
            delete state.dossier?.dossier_id;
            delete state.dossier?.dossier_access_token;
        },
        disableApiError: (state) => {
            state.dossierError.show = false;
            state.dossierError.message = "";
        },
        setDossierLoading: (state, action: PayloadAction<boolean>) => {
            state.dossierLoading = action.payload;
        },
        unsetDossier: (state) => {
            delete state.dossier;
        },
        setDossierApiError: (state, action: PayloadAction<number>) => {
            state.dossierError.show = true;
            state.dossierLoading = false;
            state.dossierError.message =
                "Sorry, er is iets foutgegaan. Code: " + action.payload;
            if (action.payload === 401) {
                localStorage.clear();
                window.location.reload();
            }
        },
        setDossierOptions: (
            state,
            action: PayloadAction<RootInterface["dossierOptions"]>,
        ) => {
            state.dossierOptions = action.payload;
        },
        setRegistrationLinkError: (state) => {
            state.registrationLink.loading = false;
            state.registrationLink.notFound = true;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(updateDossier.pending, (state) => {
            state.dossierLoading = true;
        });
        builder.addCase(updateDossier.fulfilled, (state, action) => {
            state.dossier = action.payload;
            state.dossierIsDeleted = false;
            state.dossierLoading = false;

            if (action.payload?.dossier_uuid && state.registrationLink.slug) {
                localStorage.setItem(
                    state.registrationLink.slug + "-dossier_uuid",
                    action.payload.dossier_uuid,
                );
            }

            if (
                action.payload?.dossier_access_token &&
                state.registrationLink.slug
            ) {
                localStorage.setItem(
                    state.registrationLink.slug + "-dossier_access_token",
                    action.payload.dossier_access_token,
                );
            }
        });
        builder.addCase(deleteDossier.pending, (state) => {
            state.dossierLoading = true;
        });
        builder.addCase(deleteDossier.fulfilled, (state) => {
            state.dossierIsDeleted = true;
            state.dossierLoading = false;
        });
        builder.addCase(getRegistrationLink.pending, (state) => {
            state.registrationLink.loading = true;
        });
        builder.addCase(getRegistrationLink.fulfilled, (state, action) => {
            state.registrationLink = {
                ...state.registrationLink,
                ...action.payload,
                notFound: false,
                loading: false,
            };
        });
    },
});

export const {
    setFinished,
    setDossierLoading,
    enableHelpModal,
    enableGreenCardModal,
    enableSmartPhotoModal,
    enableMobileNumberCheckModal,
    disableModals,
    toggleBovagTerms,
    setBovagToTrue,
    togglePrivacy,
    unsetDossier,
    deleteDossierId,
    setDossierApiError,
    setRegistrationLinkError,
    setGetParams,
    disableApiError,
    setDossierOptions,
    enableBovagModal,
} = rootSlice.actions;

export default rootSlice.reducer;
